import { yupResolver } from '@hookform/resolvers/yup';
import cls from 'classnames';
import React, { memo, useCallback, useEffect } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useForm } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import Select from 'react-select';

import styles from './NewDeviceCheck.module.css';
import Step from '../Step/Step';
import { StepType } from '../../../state/types';
import { State, store, useState } from '../../../state/store';
import ImeiRetry from '../../ImeiRetry/ImeiRetry';
import QuestionTooltip from '../../QuestionTooltip/QuestionTooltip';
import FormContainer from '../../FormContainer/FormContainer';
import { newDeviceCheckSchema } from '../../../state/validation';
import { convertFileToStringBase64, normalizeFileName } from '../../../utils/file';
import { commonSelectStyles } from '../../../state/constants';
import moment from 'moment';

export const SALES_START_DATE_TIMESTAMP_MS = new Date('07/26/23'); // No sales happened before this date (mm/dd/yy) 1-Apr-2023

const NewDeviceCheck: React.FC = memo(() => {
  const {
    imei,
    step,
    retailer,
    retailerOther,
    retailers,
    promotions,
    isImeiError,
    purchaseDate,
    purchaseConfirmationName,
    checkErrorMessage,
    checkErrors,
    requestSent,
    dispatch,
  } = useState(
    'imei',
    'step',
    'retailer',
    'retailerOther',
    'retailers',
    'promotions',
    'isImeiError',
    'purchaseDate',
    'purchaseConfirmationName',
    'requestSent',
    'checkErrorMessage',
    'checkErrors'
  );

  const selectedDate = purchaseDate ? new Date(purchaseDate) : null;
  const {
    register,
    trigger,
    watch,
    setValue,
    setError,
    formState: { errors, isValid },
  } = useForm<State>({
    resolver: yupResolver(newDeviceCheckSchema),
    defaultValues: {
      purchaseDate: selectedDate?.getTime(),
    },
  });

  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      return;
    }

    const token = await executeRecaptcha('NewDeviceCheckStep');
    dispatch('update', {
      recaptchaToken: token,
    });
  }, [executeRecaptcha, dispatch]);

  useEffect(() => {
    if (step === StepType.NewDeviceCheck && !requestSent) {
      handleReCaptchaVerify().then();
    }
  }, [handleReCaptchaVerify, step, requestSent]);

  if (isImeiError) {
    return <ImeiRetry />;
  }

  const prepareRegisterProps = (
    name: keyof State,
    options: { required: boolean; pattern?: RegExp } = { required: true }
  ) => {
    const registerProps = { ...register(name, options) };
    const originalImeiOnChange = registerProps.onChange;
    registerProps.onChange = (e) => {
      /**
       * Trigger validation right when user is typing and update state in case there are no errors
       */
      return originalImeiOnChange(e)
        .then(() => trigger(name))
        .then(() => {
          if (!errors[name]) {
            dispatch('update', {
              [name]: watch(name),
            });
          }
        });
    };
    return registerProps;
  };
  const purchaseConfirmationProps = prepareRegisterProps('purchaseConfirmation');
  purchaseConfirmationProps.onChange = (e) => {
    const confirmationFile: File = e.target.files[0];

    if (confirmationFile.size > 3 * 1024 * 1024) {
      setError('purchaseConfirmation', { message: 'File size should not be bigger than 3 MB.' });
      return new Promise<boolean | void>((resolve, reject) => reject('File size error'));
    }

    return convertFileToStringBase64(confirmationFile)
      .then((base64) => {
        if (!errors['purchaseConfirmation']) {
          dispatch('update', {
            purchaseConfirmation: base64,
            purchaseConfirmationName: confirmationFile.name || '',
          });
        }
      })
      .then(() => trigger('purchaseConfirmation'));
  };
  const onImeiBlur = () => trigger('imei');
  const onFileInputBlur = () => trigger('purchaseConfirmation');
  const setPurchaseDate = (date: Date) => {
    if (date) {
      try {
        const purchaseDate = date.getTime();
        setValue('purchaseDate', purchaseDate);
        trigger('purchaseDate').then(() => {
          dispatch('update', {
            purchaseDate: purchaseDate,
          });
        });
      } catch (e) {
        store.dispatch('update', {
          generalError: `${e}`,
        });
      }
    }
  };

  const retailerOption = retailer ? retailers.find((s) => s.label === retailer) : null;
  const isOtherRetailerRequired = retailerOption?.value === 'other' && !retailerOther;
  const statusDates = {
    promotionStart: moment(promotions.promotion_date_period.start, 'MM/DD/YYYY').format('Do MMMM YYYY'),
    promotionEnd: moment(promotions.promotion_date_period.end, 'MM/DD/YYYY').format('Do MMMM YYYY'),
    claimPeriodStart: moment(promotions.claim_date_period.start, 'MM/DD/YYYY').format('Do MMMM YYYY'),
    claimPeriodEnd: moment(promotions.claim_date_period.end, 'MM/DD/YYYY').format('Do MMMM YYYY'),
  };

  return (
    <Step
      stepNext={StepType.OldDeviceModel}
      stepToShow={StepType.NewDeviceCheck}
      isValid={isValid && purchaseConfirmationName !== '' && !isOtherRetailerRequired}
    >
      <div className={styles.NewDeviceCheck} data-testid="NewDeviceCheck">
        <form className="w-full bg-gray-100 rounded">
          <FormContainer header={'Your new Device'}>
            <div className="mb-6">
              <div className="md:w-full">
                <div className={'flex mx-auto max-w-xs'}>
                  <div className={'w-full'}>
                    <label className="block text-gray-500 font-bold mb-1 md:mb-0 pr-4" htmlFor="inline-imei">
                      Insert IMEI of your new Device *:
                    </label>
                    <div className={'flex'}>
                      <div className={styles.FieldValue}>
                        <input
                          {...prepareRegisterProps('imei')}
                          onBlur={onImeiBlur}
                          defaultValue={imei}
                          className={cls(
                            'appearance-none border-2 border-gray-200 rounded w-full py-2 px-2 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-blue-500'
                          )}
                          id="inline-imei"
                          type="text"
                        />
                        <br />
                        <div className="pt-2 text-red-400 text-center">
                          <div key="imei_error">{errors.imei?.message}</div>
                          {(checkErrors['imei'] || []).map((serverError, index) => (
                            <div key={index}>{serverError}</div>
                          ))}
                        </div>
                      </div>
                      <div className={styles.FieldQuestion}>
                        <QuestionTooltip>
                          <p className="p-2 m-0">
                            Dial <strong>*#06#</strong> from your device
                          </p>
                          <p className="p-2 m-0">An IMEI is 15 character long</p>
                          <p className="p-2 m-0">
                            Alternatively, Go to <strong>Settings</strong>, Select <strong>About phone</strong> or{' '}
                            <strong>About device</strong>
                          </p>
                          <p className="p-2 m-0">
                            <strong>
                              Otherwise, IMEI is located underneath the battery or inscribed on the back of the mobile.
                            </strong>
                          </p>
                        </QuestionTooltip>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-6">
              <div className="md:w-full">
                <div className={'flex mx-auto max-w-xs'}>
                  <div className={'w-full'}>
                    <label className="block text-gray-500 font-bold mb-1 md:mb-0 pr-4" htmlFor="inline-email">
                      Choose a participating retailer *:
                    </label>
                    <Select
                      inputId="retailer"
                      value={retailerOption}
                      onBlur={() => trigger('retailer')}
                      placeholder="Please Choose One"
                      onChange={(option) => {
                        setValue('brand', option?.label || '', { shouldValidate: true });
                        dispatch('update', {
                          retailer: option?.label || '',
                        });
                      }}
                      options={retailers}
                      styles={commonSelectStyles}
                    />
                    <br />
                    <div className="pt-2 text-center text-red-400">{errors.retailer?.message}</div>
                  </div>
                </div>
              </div>
              {retailerOption?.value === 'other' && (
                <div className={'flex mx-auto max-w-xs'}>
                  <div className={'w-full'}>
                    <input
                      id="retailerOther"
                      {...prepareRegisterProps('retailerOther')}
                      onBlur={() => trigger('retailerOther')}
                      defaultValue={retailerOther}
                      className={cls(
                        'appearance-none border-2 border-gray-200 rounded w-full py-2 px-2 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-blue-500'
                      )}
                      type="text"
                    />
                    <br />
                    <div className="pt-2 text-center text-red-400">
                      {isOtherRetailerRequired && <div>This field is required</div>}
                      {errors.retailerOther?.message}
                    </div>
                  </div>
                </div>
              )}
              <div className="pt-2 text-center text-red-400">
                {(checkErrors['retailer'] || []).map((serverError, index) => (
                  <div key={index}>{serverError}</div>
                ))}
              </div>
            </div>
            <div className="mb-6">
              <div className="md:w-full">
                <div className={'flex mx-auto max-w-xs'}>
                  <div className={'w-full'}>
                    <label className="block text-gray-500 font-bold mb-1 md:mb-0 pr-4" htmlFor="confirmationPurchase">
                      Proof of Purchase *:
                    </label>
                    <div className="input-group">
                      <input
                        id="confirmationPurchase"
                        {...purchaseConfirmationProps}
                        onBlur={onFileInputBlur}
                        type="file"
                        accept=".png,.jpg,.jpeg,.pdf,image/png,image/jpeg,application/pdf"
                        className="custom-file-input"
                      />
                      <label
                        className="custom-file-label border-gray-200 rounded border-2"
                        htmlFor="confirmationPurchase"
                      >
                        {purchaseConfirmationName ? 'Select another file ...' : 'Upload Proof of Purchase'}
                      </label>
                      <div className={styles.FieldQuestion}>
                        <QuestionTooltip id="file-tooltip">
                          <p className="p-2 m-0">File types allowed are .jpg, .jpeg, .png & .pdf.</p>
                          <p className="p-2 m-0">File size limit is 3mb</p>
                        </QuestionTooltip>
                      </div>
                    </div>
                    <div
                      className={cls(
                        'pt-2 text-left',
                        { 'text-gray-500': !purchaseConfirmationName },
                        { 'text-dark': !!purchaseConfirmationName }
                      )}
                    >
                      {purchaseConfirmationName ? normalizeFileName(purchaseConfirmationName) : ''}
                    </div>
                    <div className="pt-2 text-red-400 text-center">{errors.purchaseConfirmation?.message}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-6">
              <div className="md:w-full">
                <div className={'flex mx-auto max-w-xs'}>
                  <div className={'w-full'}>
                    <label className="block text-gray-500 font-bold mb-1 md:mb-0 pr-4" htmlFor="date-input">
                      Purchase date *:
                    </label>
                    <div className={'flex'}>
                      <div className={styles.FieldValue}>
                        <DatePicker
                          id="date-input"
                          className={cls('border-gray-200 rounded border-2', 'w-full')}
                          minDate={new Date(promotions.purchase_date_period.start)}
                          maxDate={
                            new Date(promotions.purchase_date_period.end) < new Date()
                              ? new Date(promotions.purchase_date_period.end)
                              : new Date()
                          }
                          selected={selectedDate}
                          onChange={setPurchaseDate}
                          dateFormat="dd/MM/yyyy"
                        />
                      </div>
                      <div className={styles.FieldQuestion}>
                        <QuestionTooltip id="data-tooltip">
                          <p className="p-2 m-0">Key Promotion Dates:</p>
                          <p className="p-2 m-0">
                            The Promotion will commence at 00:01 (BST) {statusDates.promotionStart} and shall close at
                            23.59 (BST) on {statusDates.promotionEnd} (the “Promotion Period”).
                          </p>
                          <p className="p-2 m-0">
                            Promotion Period {statusDates.promotionStart} - {statusDates.promotionEnd}.
                          </p>
                          <p className="p-2 m-0">
                            Claim Period {statusDates.claimPeriodStart} - {statusDates.claimPeriodEnd}.
                          </p>
                        </QuestionTooltip>
                      </div>
                    </div>
                    <div className="pt-2 text-red-400 text-center">
                      <div key="purchased_date_error">{checkErrors['purchased_date']}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </FormContainer>
        </form>
        <div className="pt-2 text-red-400 text-center">{checkErrorMessage}</div>
        <div className="pt-2 text-center text-red-400">
          {(checkErrors['g_recaptcha_response'] || []).map((serverError, index) => (
            <div key={index}>{serverError}</div>
          ))}
        </div>
      </div>
    </Step>
  );
});

export default NewDeviceCheck;
